import { Link as RouterLink } from 'react-router'
import PropTypes from 'prop-types'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Fade from '@mui/material/Fade'
import { alpha } from '@mui/material/styles'

import useFormatMessage from '@hooks/useFormatMessage'
import { backButton } from '@utils/StylesHelper'
import { useBackIntercept } from '@components/LeavePrompt'

import { backPropType, columnWidth, PAGE_WIDTH, widthPropType } from './Screen.props'
import useScrolledUnder from './useScrolledUnder'

export const PAGE_BAR_HEIGHT = 56

// A progressive blur: every layer blurs harder than the one beneath it and is masked to a
// shorter band, so the radius itself ramps to nothing by the bottom edge. A single layer at
// a fixed radius can only fade its opacity, which leaves a ghost of the page over the page.
const BLUR_LAYERS = [
  { blur: 1, stops: '#000 0%, #000 70%, rgba(0, 0, 0, 0) 100%' },
  { blur: 2, stops: '#000 0%, #000 45%, rgba(0, 0, 0, 0) 78%' },
  { blur: 5, stops: '#000 0%, #000 25%, rgba(0, 0, 0, 0) 58%' },
  { blur: 12, stops: '#000 0%, #000 8%, rgba(0, 0, 0, 0) 38%' },
]

PageBar.propTypes = {
  /** Width of the inner column, matched to the page content below it */
  width: widthPropType,

  /** Trailing controls, kept in their own slot so they cannot pull the title off centre */
  actions: PropTypes.node,

  /** How the way back behaves. See `backPropType`. */
  back: backPropType,

  /** Bar contents, usually the title */
  children: PropTypes.node,

  /** Whether the bar is the page's banner. Off in a dialog, which carries its own name. */
  landmark: PropTypes.bool,
}

/**
 * The sticky bar at the top of a screen.
 *
 * A plain box rather than an `AppBar` and `Toolbar`, which the theme forces to 64px. The
 * back control and the actions sit in equal width slots either side of the contents, so a
 * centred title lands on the true centre line whatever sits beside it.
 *
 * @param {object} props - The component props.
 * @returns {JSX.Element} The rendered bar.
 */
export default function PageBar({ width = PAGE_WIDTH, actions, back, landmark = true, children }) {
  const { to = -1, replace = false, onBack } = back || {}
  const formatMessage = useFormatMessage()
  const intercept = useBackIntercept(to)
  const [ref, scrolled] = useScrolledUnder()

  return (
    <Box ref={ref} sx={styles.bar} role={landmark ? 'banner' : undefined}>
      {/* Unmounted at rest — four stacked backdrop filters are not free to keep running —
          so this needs a transition that mounts at zero. An element inserted at its final
          opacity has nothing to animate, which is why a CSS one would not do. */}
      <Fade in={scrolled} timeout={{ enter: 200, exit: 150 }} unmountOnExit>
        <Box aria-hidden="true" sx={styles.material}>
          {BLUR_LAYERS.map(({ blur, stops }) => (
            <Box key={blur} sx={styles.layer(blur, stops)} />
          ))}
          <Box sx={styles.tint} />
        </Box>
      </Fade>

      <Container maxWidth={false} sx={[styles.inner, columnWidth(width)]} disableGutters>
        <Box sx={styles.slot('flex-start')}>
          {back !== false && (
            <Button
              {...(onBack ? { onClick: onBack } : { component: RouterLink, to, replace, onClick: intercept })}
              data-testid="page-header-back-btn"
              aria-label={formatMessage('navigation.back')}
              startIcon={<ArrowBackIcon fontSize="small" />}
              sx={styles.back}
            >
              <Box component="span" sx={styles.backLabel}>
                {formatMessage('navigation.back')}
              </Box>
            </Button>
          )}
        </Box>

        <Box sx={styles.content}>{children}</Box>

        <Box sx={styles.slot('flex-end')}>{actions}</Box>
      </Container>
    </Box>
  )
}

const styles = {
  // No colour of its own at rest: what shows through is the page's own ground, white on
  // most screens and the animated wash on the ones that have one.
  bar: {
    position: 'sticky',
    top: 0,
    zIndex: (theme) => theme.zIndex.appBar,
    flexShrink: 0,
    px: 2,
  },
  material: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
  },
  layer: (blur, stops) => ({
    position: 'absolute',
    inset: 0,
    backdropFilter: `blur(${blur}px)`,
    WebkitBackdropFilter: `blur(${blur}px)`,
    maskImage: `linear-gradient(to bottom, ${stops})`,
    WebkitMaskImage: `linear-gradient(to bottom, ${stops})`,
  }),
  // Paper over the blur, thinning on the same curve. The blur alone would keep the colour
  // of whatever is underneath; this is what leaves the title on a clean ground.
  tint: {
    position: 'absolute',
    inset: 0,
    backgroundImage: (theme) => {
      const paper = theme.palette.background.paper
      return `linear-gradient(to bottom, ${alpha(paper, 0.94)} 0%, ${alpha(paper, 0.78)} 45%, ${alpha(paper, 0)} 100%)`
    },
  },
  inner: {
    // Above the material, which is positioned and would otherwise paint over the title
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minHeight: PAGE_BAR_HEIGHT,
  },
  // Equal basis on both sides is what puts the title on the centre line
  slot: (justifyContent) => ({
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    justifyContent,
  }),
  content: {
    flex: '0 1 auto',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minWidth: 0,
    textAlign: 'center',
  },
  // A bare circle on a wide screen is a 40px target adrift at the edge of a window. Given
  // the room, it says what it does; a phone has none, so there it stays a circle.
  back: {
    ...backButton,
    minWidth: 0,
    height: 40,
    width: { xs: 40, md: 'auto' },
    px: { xs: 0, md: 2 },
    borderRadius: { xs: '50%', md: 100 },
    fontWeight: 'bold',
    // The stock start-icon gutter only makes sense once there is a word beside it
    '& .MuiButton-startIcon': { ml: 0, mr: { xs: 0, md: 1 } },
  },
  backLabel: {
    display: { xs: 'none', md: 'inline' },
  },
}
